import * as fs from "node:fs";
import * as path from "node:path";
import { parse as parseYaml } from "yaml";
import type { TokenCredential } from "@azure/identity";
import type { ReasoningEffort } from "@azure/ai-projects";
import { renderPersonaBody } from "./persona-render.js";
import { createPromptVersion } from "./foundry-agents.js";
import { LocalCliError } from "./errors.js";

interface AdvisorFrontmatter {
  version?: string | number;
  targets?: { foundry?: { model?: string; reasoning?: { effort?: string } } };
  "fillable-fields"?: { name: string; default?: string }[];
}

/**
 * Resolve the personas directory root starting from `hint`, walking up until a
 * `personas/` directory is found. This allows the function to be called with
 * `process.cwd()` even when the cwd is a subdirectory of the repo checkout
 * (e.g. `apps/cli/` in the monorepo layout).
 *
 * Returns `null` when neither `hint` nor any ancestor contains a `personas/` dir.
 */
function findPersonasRoot(hint: string): string | null {
  let dir = path.resolve(hint);
  const fsRoot = path.parse(dir).root;
  // Walk up from `dir` toward the filesystem root, checking each level.
  for (;;) {
    if (fs.existsSync(path.join(dir, "personas"))) return dir;
    const parent = path.dirname(dir);
    if (parent === dir || dir === fsRoot) return null;
    dir = parent;
  }
}

/**
 * Deploy an advisor persona as a Foundry prompt agent.
 *
 * Reads model + reasoning from `targets.foundry.*` in the persona frontmatter,
 * builds the instructions via `renderPersonaBody` (substituting any
 * `fillable-fields` defaults so no `{{placeholder}}` residue is left), then
 * calls `createPromptVersion` under the given `agentName`.
 *
 * The `repoRoot` may be the exact checkout root or any subdirectory — the function
 * walks up to locate the `personas/` directory. This lets callers pass
 * `process.cwd()` safely regardless of which subdirectory they are in.
 *
 * The intake persona (`startup-advisor-intake`) has no fillable-fields, so
 * `deploySimpleStacey` can safely delegate here with an empty defaults map.
 */
export async function deployPromptAdvisor(args: {
  credential: TokenCredential;
  endpoint: string;
  repoRoot: string;
  persona: string;
  agentName: string;
  /** Override the model from frontmatter (optional). */
  model?: string;
  /** Override specific fillable-field values; merged over frontmatter defaults (caller wins). */
  fieldOverrides?: Record<string, string>;
}): Promise<string> {
  const resolvedRoot = findPersonasRoot(args.repoRoot);
  const personaPath = resolvedRoot
    ? path.join(resolvedRoot, "personas", args.persona, "persona.md")
    : path.join(args.repoRoot, "personas", args.persona, "persona.md");

  let raw: string;
  try {
    raw = fs.readFileSync(personaPath, "utf8");
  } catch (e) {
    throw new LocalCliError({
      code: "ADVISOR_PERSONA_MISSING",
      message: `Could not read the persona at '${personaPath}'.`,
      hint: "Run from your m8t repo checkout, or pass --repo-root.",
      cause: e,
    });
  }

  const fmMatch = /^---\n([\s\S]*?)\n---/.exec(raw);
  const fm: AdvisorFrontmatter = fmMatch ? (parseYaml(fmMatch[1]) as AdvisorFrontmatter) : {};

  const model = args.model ?? fm.targets?.foundry?.model;
  if (!model) {
    throw new LocalCliError({
      code: "ADVISOR_NO_MODEL",
      message: `Persona '${args.persona}' has no targets.foundry.model.`,
      hint: `Set targets.foundry.model in personas/${args.persona}/persona.md.`,
    });
  }

  const VALID_EFFORTS: ReadonlySet<string> = new Set([
    "none",
    "minimal",
    "low",
    "medium",
    "high",
    "xhigh",
  ]);
  const rawEffort = fm.targets?.foundry?.reasoning?.effort;
  if (rawEffort !== undefined && !VALID_EFFORTS.has(rawEffort)) {
    throw new LocalCliError({
      code: "ADVISOR_BAD_EFFORT",
      message: `Invalid reasoning.effort '${rawEffort}' in persona '${args.persona}'. Valid values: none, minimal, low, medium, high, xhigh.`,
    });
  }
  const reasoningEffort = rawEffort as ReasoningEffort | undefined;
  const personaVersion = fm.version === undefined ? null : String(fm.version);

  // Build a values map from fillable-fields defaults, then let caller overrides win.
  const fillableFields = fm["fillable-fields"] ?? [];
  const valuesMap: Record<string, string> = {};
  for (const field of fillableFields) {
    if (field.default !== undefined) valuesMap[field.name] = field.default;
  }
  for (const [k, v] of Object.entries(args.fieldOverrides ?? {})) valuesMap[k] = v;

  const instructions = renderPersonaBody(personaPath, valuesMap);

  return createPromptVersion({
    credential: args.credential,
    endpoint: args.endpoint,
    name: args.agentName,
    model,
    instructions,
    reasoningEffort,
    metadata: { persona: args.persona, personaVersion, fillableFieldValues: JSON.stringify(valuesMap) },
  });
}
